/**
 * CommentAiAssistModal — IA co-reviewer (Doc 21 Fase 7.e).
 *
 * Flow:
 *
 *   1. Quien puede editar el artifact (dueño actual ∪ líder ∪ admin,
 *      OT-0016) abre el modal sobre un comment `pending|addressed`. El
 *      caller no lo monta para nadie más, así que generar, aplicar y
 *      descartar no se vuelven a gatear aquí.
 *   2. Ve el comment del reviewer + opcionalmente escribe instrucciones
 *      extra ("hazlo más corto").
 *   3. Click "Generar" → POST /comments/{id}/ai-suggest → spinner.
 *   4. Result view: diff inline-unified entre `section_snapshot` (lo
 *      que el reviewer vio) y `suggested_text` (la propuesta IA).
 *   5. Opciones del result:
 *        · "Aplicar"  → POST .../apply + callback al caller con
 *          `suggested_text` (el editor lo inserta en el draft).
 *        · "Refinar"  → vuelve al input, pre-llena con la última
 *          instrucción, conserva `previous_response_id` (depth+1).
 *          Cap server-side en 3 refinements.
 *        · "Cerrar"   → descarta el modal sin persistir apply.
 *
 * Decisiones UX (Doc 21 Fase 7 §"Decisiones UX"):
 *
 *   · Verdict (`agree/disagree/partial`) NO se muestra. Backend lo
 *     persiste pero el response del API NO lo trae en MVP.
 *   · Costo se muestra en TOKENS (suma input+output), no en USD —
 *     evita complejidad del markup BYOK y no genera fricción.
 *   · El historial de iteraciones se recarga al abrir el modal — si
 *     el user cerró y vuelve, ve dónde se quedó.
 *
 * Aplicar NO toca artifact_versions: el editor del frontend recibe
 * `suggested_text` vía callback y decide dónde insertarlo (en MVP, el
 * caller lo copia al clipboard del user; en futuras fases se inyecta
 * inline en la sección anclada del editor).
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  ArrowLeft,
  BookOpen,
  Check,
  Copy,
  FileText,
  Loader2,
  RefreshCw,
  Search,
  Sparkles,
} from 'lucide-react'

import type { WaitingPayload } from '../../../api/agents'
import { ApiError } from '../../../api/client'
import { isVersionLockError, markVersionLocked } from '../../../lib/versionLock'
import {
  applyCommentAiSuggestion,
  createCommentAiSuggestion,
  discardCommentAiSuggestions,
  listCommentAiSuggestions,
  type ArtifactComment,
  type CommentAiSuggestion,
  type CommentAiToolTraceEntry,
} from '../../../api/comments'
import { useAccessToken } from '../../../auth/useAccessToken'
import { useUsageRecordByRequest } from '../../../hooks/useUsageRecordByRequest'
import { pushToast } from '../../../stores/toastStore'
import { Dialog } from '../../Dialog'
import { aiWaitingFrom } from '../../../lib/aiWaiting'
import { GroundingFindingsSection } from '../../ai/GroundingFindings'
import { diffLines, summarizeDiff } from '../../../lib/lineDiff'
import { AiWaitingNotice } from '../../ai/AiStreamNotices'
import { valueToReadableText } from './commentAnchorText'

const REFINEMENT_CAP = 3
const USER_INSTRUCTIONS_MAX = 1000

interface CommentAiAssistModalProps {
  open: boolean
  onClose: () => void
  /** Artefacto del comentario: lo necesita el bloqueo por estado
   *  (`markVersionLocked`) para marcar la versión en caché. */
  artifactId: string
  comment: ArtifactComment
  sectionTitle: string
  /** Llamado cuando el user clickea "Aplicar" en una sugerencia.
   *  El caller decide qué hacer con el texto (típicamente: copiarlo al
   *  clipboard + toast, o inyectarlo en el editor TipTap activo). */
  onApply: (suggestion: CommentAiSuggestion) => void
}

export function CommentAiAssistModal({
  open,
  onClose,
  artifactId,
  comment,
  sectionTitle,
  onApply,
}: CommentAiAssistModalProps) {
  const { t } = useTranslation(['projects', 'errors'])
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()

  // ─── State ──────────────────────────────────────────────────────────
  // El padre desmonta este componente al cerrar el modal, así que todo
  // este state se resetea automáticamente — no necesitamos effect.
  //
  // `explicitInputMode` se setea cuando el user clickea "Refinar":
  // queremos mostrar el input phase aunque ya haya una sugerencia
  // activa. Sin este flag, el phase se deriva: hay sugerencia ⇒
  // result, no hay ⇒ input. Esto evita un effect de "auto-jump"
  // (lint: react-hooks/set-state-in-effect).
  const [explicitInputMode, setExplicitInputMode] = useState(false)
  const [userInstructions, setUserInstructions] = useState('')
  const [explicitResponseId, setExplicitResponseId] = useState<string | null>(
    null,
  )
  // Doc 21 Fase 7.g — "Empezar de cero": cuando el user llega al cap
  // de 3 refinements (o simplemente quiere descartar la cadena actual
  // y arrancar de nuevo), este flag fuerza el siguiente generate a
  // pasar `previous_response_id=null`. El backend crea una nueva
  // cadena con depth=0 y el modal salta a esa como sugerencia activa.
  // Se resetea automáticamente tras un generate exitoso.
  const [discardChain, setDiscardChain] = useState(false)
  // Request en curso de la IA (SSE): al desmontarse el modal se corta la
  // conexión y el backend cancela el agente (OT-0040).
  const generateAbortRef = useRef<AbortController | null>(null)
  const [waiting, setWaiting] = useState<WaitingPayload | null>(null)

  useEffect(() => () => generateAbortRef.current?.abort(), [])

  // ─── Queries ────────────────────────────────────────────────────────
  const historyQuery = useQuery({
    queryKey: ['comments', comment.id, 'ai-suggest'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listCommentAiSuggestions(accessToken, comment.id)
    },
    enabled: !!accessToken && open,
    staleTime: 0,
  })

  const history = useMemo(
    () => historyQuery.data ?? [],
    [historyQuery.data],
  )

  // Doc 21 Fase 7.g — filtrar propuestas descartadas del auto-resume.
  // Las rows siguen en `history` (necesarias para audit/token totals),
  // pero el modal NO debe mostrarlas como propuesta activa al reabrir.
  const activeHistory = useMemo(
    () => history.filter((r) => r.discarded_at === null),
    [history],
  )

  // Derivar la sugerencia activa: si el user clickeó una explícita
  // (después de Generate), usa esa; en su defecto, la última NO
  // descartada del historial. Cero effects.
  const effectiveResponseId =
    explicitResponseId ??
    (activeHistory.length > 0
      ? activeHistory[activeHistory.length - 1]!.id
      : null)
  const currentSuggestion = useMemo(
    () => activeHistory.find((r) => r.id === effectiveResponseId) ?? null,
    [activeHistory, effectiveResponseId],
  )

  // Phase = result salvo que el user pidió explícitamente input
  // (refinar) o no hay sugerencias todavía.
  const phase: 'input' | 'result' =
    currentSuggestion === null || explicitInputMode ? 'input' : 'result'

  const refinementCount = currentSuggestion?.refinement_depth ?? -1
  const canRefine = refinementCount < REFINEMENT_CAP

  // ─── Mutations ──────────────────────────────────────────────────────
  const generateMutation = useMutation({
    mutationFn: async (input: {
      userInstructions: string | null
      previousResponseId: string | null
    }) => {
      if (!accessToken) throw new Error('no_access_token')
      const body: {
        user_instructions?: string | null
        previous_response_id?: string | null
      } = {}
      if (input.userInstructions !== null) {
        body.user_instructions = input.userInstructions
      }
      if (input.previousResponseId !== null) {
        body.previous_response_id = input.previousResponseId
      }
      const ctrl = new AbortController()
      generateAbortRef.current = ctrl
      return createCommentAiSuggestion(accessToken, comment.id, body, {
        signal: ctrl.signal,
        onEvent: (event) => setWaiting(aiWaitingFrom(event)),
      })
    },
    onSettled: () => setWaiting(null),
    onSuccess: (response) => {
      setExplicitResponseId(response.id)
      setExplicitInputMode(false)
      setUserInstructions('')
      // El nuevo response es la "current suggestion"; reseteamos el
      // flag para que el siguiente Refinar (si aplica) sí encadene.
      setDiscardChain(false)
      void queryClient.invalidateQueries({
        queryKey: ['comments', comment.id, 'ai-suggest'],
      })
    },
    onError: (err) => {
      if (generateAbortRef.current?.signal.aborted) return
      const code = err instanceof ApiError ? err.code : null
      // Sin copy propia para el código, el mensaje que ya llega traducido
      // (backend o `STREAM_INTERRUPTED`); nunca el texto crudo de la red.
      const fallback =
        (err instanceof ApiError ? err.body?.message : undefined) ??
        t('projects:comments_ai_modal.toast.generate_failed')
      const message = code
        ? t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })
        : err instanceof Error
          ? err.message
          : fallback
      pushToast({ kind: 'critical', message, duration: 6000 })
    },
  })

  const applyMutation = useMutation({
    mutationFn: async (suggestion: CommentAiSuggestion) => {
      if (!accessToken) throw new Error('no_access_token')
      return applyCommentAiSuggestion(accessToken, comment.id, suggestion.id)
    },
    onSuccess: (applied) => {
      void queryClient.invalidateQueries({
        queryKey: ['comments', comment.id, 'ai-suggest'],
      })
      onApply(applied)
      onClose()
    },
    onError: (err) => {
      // OT-0064 — la versión del comentario dejó de ser editable (entró a
      // revisión, se aprobó o se descartó): se bloquea como cualquier otro
      // rechazo por estado. El aviso sigue el camino de abajo (`errors:`).
      if (isVersionLockError(err)) {
        markVersionLocked(queryClient, artifactId, comment.artifact_version_id)
        onClose()
      }
      const code = err instanceof ApiError ? err.code : null
      const fallback = t('projects:comments_ai_modal.toast.apply_failed')
      const message = code
        ? t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })
        : err instanceof Error
          ? err.message
          : fallback
      pushToast({ kind: 'critical', message, duration: 6000 })
    },
  })

  // Doc 21 Fase 7.g — Cancelar UX. Descarta toda la cadena de propuestas
  // no-aplicadas en BD y cierra el modal. La próxima vez que el user
  // abra el modal verá pantalla fresh (sin auto-resume).
  const discardMutation = useMutation({
    mutationFn: async () => {
      if (!accessToken) throw new Error('no_access_token')
      return discardCommentAiSuggestions(accessToken, comment.id)
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: ['comments', comment.id, 'ai-suggest'],
      })
      if (result.discarded_count > 0) {
        pushToast({
          kind: 'info',
          message: t('projects:comments_ai_modal.toast.discarded', {
            count: result.discarded_count,
          }),
          duration: 4000,
        })
      }
      onClose()
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : null
      const fallback = t('projects:comments_ai_modal.toast.discard_failed')
      const message = code
        ? t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })
        : err instanceof Error
          ? err.message
          : fallback
      pushToast({ kind: 'critical', message, duration: 6000 })
    },
  })

  // ─── Derived values for diff render ─────────────────────────────────
  // Doc 24 rework — preferir `currentSuggestion.section_current_text`
  // (serializado en el backend con las mismas columnas/labels que
  // `suggested_text`) para que el diff visual compare apples-to-apples.
  // Fallback al helper local solo para rows legacy pre-rework donde el
  // campo es null. Sin esto, structured_table v2 (`{rows: [...]}`)
  // quedaba vacío del lado izquierdo y todo aparecía como adición.
  const originalText = useMemo(() => {
    const fromApi = currentSuggestion?.section_current_text
    if (typeof fromApi === 'string' && fromApi.length > 0) {
      return fromApi
    }
    return valueToReadableText(comment.section_snapshot) ?? ''
  }, [currentSuggestion, comment.section_snapshot])

  const diff = useMemo(() => {
    if (!currentSuggestion) return []
    return diffLines(originalText, currentSuggestion.suggested_text)
  }, [originalText, currentSuggestion])

  const diffStats = useMemo(() => summarizeDiff(diff), [diff])

  // ─── Handlers ───────────────────────────────────────────────────────
  // Generate es polimórfico:
  //   · Sin `currentSuggestion` → primera petición.
  //   · Con `currentSuggestion` y `!discardChain` → refinement
  //     (depth+1, encadenado).
  //   · Con `currentSuggestion` y `discardChain` → nueva cadena con
  //     depth=0 (ignora todo lo anterior).
  function handleGenerate() {
    const trimmed = userInstructions.trim()
    generateMutation.mutate({
      userInstructions: trimmed.length > 0 ? trimmed : null,
      previousResponseId: discardChain
        ? null
        : currentSuggestion?.id ?? null,
    })
  }

  // "Empezar de cero" — el user llegó al cap o quiere descartar la
  // cadena. Lleva al input phase con campo vacío y flag activado.
  function handleStartFresh() {
    setDiscardChain(true)
    setExplicitInputMode(true)
    setUserInstructions('')
  }

  function handleApply() {
    if (!currentSuggestion) return
    applyMutation.mutate(currentSuggestion)
  }

  // ─── Render ─────────────────────────────────────────────────────────
  const isWorking =
    generateMutation.isPending ||
    applyMutation.isPending ||
    discardMutation.isPending

  // "Cancelar" — descarta la cadena y cierra. Solo aplicable cuando ya
  // hay propuestas activas que descartar; si no hay nada, basta con
  // un onClose normal.
  function handleCancel() {
    if (activeHistory.length === 0) {
      onClose()
      return
    }
    discardMutation.mutate()
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isWorking) onClose()
      }}
      title={t('projects:comments_ai_modal.title')}
      size="lg"
      footer={
        phase === 'input' ? (
          <>
            {/* "Cancelar" descarta la cadena (si hay) y cierra. Si no
                hay propuestas activas, equivale a un cerrar simple. */}
            {generateMutation.isPending ? (
              /* Detiene la IA: el backend cancela al agente al cerrarse
                 la conexión; el modal queda en el paso de instrucciones. */
              <button
                type="button"
                onClick={() => generateAbortRef.current?.abort()}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-ink-muted hover:text-critical rounded"
              >
                {t('projects:comments_ai_modal.actions.abort')}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleCancel}
                disabled={isWorking}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-ink-muted hover:text-critical rounded disabled:opacity-50"
                title={
                  activeHistory.length > 0
                    ? t('projects:comments_ai_modal.actions.cancel_hint')
                    : undefined
                }
              >
                {discardMutation.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : null}
                {t('projects:comments_ai_modal.actions.cancel')}
              </button>
            )}
            <button
              type="button"
              onClick={handleGenerate}
              disabled={isWorking}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand hover:bg-brand-deep text-canvas text-xs rounded disabled:opacity-50"
            >
              {generateMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5" />
              )}
              {/* Etiqueta del botón de generate: depende del modo
                  (primera vez / refinement / fresh). */}
              {(() => {
                if (currentSuggestion === null) {
                  return t('projects:comments_ai_modal.actions.generate')
                }
                if (discardChain) {
                  return t(
                    'projects:comments_ai_modal.actions.generate_fresh',
                  )
                }
                return t('projects:comments_ai_modal.actions.regenerate')
              })()}
            </button>
          </>
        ) : (
          <ResultFooter
            canRefine={canRefine}
            isWorking={isWorking}
            onBack={() => setExplicitInputMode(true)}
            onApply={handleApply}
            onStartFresh={handleStartFresh}
            onCancel={handleCancel}
            applyPending={applyMutation.isPending}
            discardPending={discardMutation.isPending}
            t={t}
          />
        )
      }
    >
      <div className="space-y-4">
        {/* Header con sección anclada + nivel del refinement. */}
        <header className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle">
            {sectionTitle}
          </p>
          {phase === 'result' && currentSuggestion ? (
            <RefinementBadge
              depth={currentSuggestion.refinement_depth}
              cap={REFINEMENT_CAP}
              t={t}
            />
          ) : null}
        </header>

        {/* Comment del reviewer — siempre visible (contexto). */}
        <section className="rounded-md border border-line bg-surface-muted px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider font-medium text-ink-subtle mb-1">
            {t('projects:comments_ai_modal.reviewer_comment')}
          </p>
          <p className="text-xs text-ink whitespace-pre-wrap break-words">
            {comment.body}
          </p>
          {comment.excerpt ? (
            <blockquote className="mt-2 pl-2 border-l-2 border-line text-[11px] italic text-ink-subtle whitespace-pre-wrap break-words">
              {comment.excerpt}
            </blockquote>
          ) : null}
        </section>

        {historyQuery.isLoading ? (
          <div className="flex items-center justify-center py-6 text-ink-subtle">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            <span className="text-xs">
              {t('projects:comments_ai_modal.loading')}
            </span>
          </div>
        ) : phase === 'input' ? (
          <>
            <InputPhase
              instructions={userInstructions}
              onChange={setUserInstructions}
              isRefining={currentSuggestion !== null && !discardChain}
              discardChain={discardChain}
              disabled={isWorking}
              t={t}
            />
            <AiWaitingNotice waiting={waiting} />
          </>
        ) : (
          <ResultPhase
            suggestion={currentSuggestion}
            diff={diff}
            diffStats={diffStats}
            t={t}
            onCopySuggested={(text) => {
              void copyToClipboard(text)
              pushToast({
                kind: 'success',
                message: t('projects:comments_ai_modal.toast.copied'),
                duration: 3000,
              })
            }}
          />
        )}
      </div>
    </Dialog>
  )
}

// ─── Subcomponents ────────────────────────────────────────────────────────
function InputPhase({
  instructions,
  onChange,
  isRefining,
  discardChain,
  disabled,
  t,
}: {
  instructions: string
  onChange: (v: string) => void
  isRefining: boolean
  discardChain: boolean
  disabled: boolean
  t: ReturnType<typeof useTranslation>['t']
}) {
  return (
    <section className="space-y-2">
      {discardChain ? (
        <div className="rounded-md border border-brand/40 bg-brand-soft/40 px-3 py-2 text-[11px] text-brand">
          {t('projects:comments_ai_modal.start_fresh_banner')}
        </div>
      ) : null}
      <label htmlFor="ai-instructions" className="block">
        <span className="text-xs font-medium text-ink">
          {discardChain
            ? t('projects:comments_ai_modal.instructions_label')
            : isRefining
              ? t('projects:comments_ai_modal.instructions_label_refine')
              : t('projects:comments_ai_modal.instructions_label')}
        </span>
        <span className="ml-2 text-[10px] text-ink-subtle">
          {t('projects:comments_ai_modal.instructions_optional')}
        </span>
      </label>
      <textarea
        id="ai-instructions"
        value={instructions}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        maxLength={USER_INSTRUCTIONS_MAX}
        placeholder={t('projects:comments_ai_modal.instructions_placeholder')}
        disabled={disabled}
        className="w-full bg-canvas border border-line rounded px-2.5 py-2 text-xs focus:outline-none focus:border-brand disabled:opacity-50"
      />
      <p className="text-[10px] text-ink-subtle text-right">
        {t('projects:comments_ai_modal.chars_count', {
          n: instructions.length,
          max: USER_INSTRUCTIONS_MAX,
        })}
      </p>
    </section>
  )
}

function ResultPhase({
  suggestion,
  diff,
  diffStats,
  t,
  onCopySuggested,
}: {
  suggestion: CommentAiSuggestion | null
  diff: ReturnType<typeof diffLines>
  diffStats: ReturnType<typeof summarizeDiff>
  t: ReturnType<typeof useTranslation>['t']
  onCopySuggested: (text: string) => void
}) {
  if (suggestion === null) {
    return (
      <div className="flex items-center justify-center py-6 text-ink-subtle">
        <AlertCircle className="w-4 h-4 mr-2" />
        <span className="text-xs">
          {t('projects:comments_ai_modal.empty')}
        </span>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Reasoning — explicación corta del cambio. */}
      {suggestion.reasoning ? (
        <section className="rounded-md border border-brand/30 bg-brand-soft/40 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider font-medium text-brand mb-1">
            {t('projects:comments_ai_modal.reasoning_label')}
          </p>
          <p className="text-xs text-ink whitespace-pre-wrap break-words">
            {suggestion.reasoning}
          </p>
        </section>
      ) : null}

      {/* Tool trace — qué consultó la IA antes de proponer (Fase 7.g Paso 1). */}
      <ToolTraceList trace={suggestion.tool_trace} t={t} />

      {/* Diff inline-unified */}
      <section>
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[10px] uppercase tracking-wider font-medium text-ink-subtle">
            {t('projects:comments_ai_modal.diff_label')}
          </p>
          <div className="flex items-center gap-2 text-[10px] font-mono">
            <span className="text-official">+{diffStats.added}</span>
            <span className="text-critical">−{diffStats.removed}</span>
          </div>
        </div>
        <DiffView diff={diff} t={t} />
      </section>

      {/* OT-0049 — observaciones de la verificación por oración de la
          propuesta, antes de que el autor decida aplicar. */}
      <GroundingFindingsSection report={suggestion.grounding} />

      {/* Footer con tokens + copy-clipboard helper */}
      <footer className="flex items-center justify-between gap-2 pt-1 text-[10px] text-ink-subtle">
        <ResultFooterTokens
          requestId={suggestion.request_id}
          fallbackInput={suggestion.tokens_input}
          fallbackOutput={suggestion.tokens_output}
          t={t}
        />
        <button
          type="button"
          onClick={() => onCopySuggested(suggestion.suggested_text)}
          className="inline-flex items-center gap-1 text-ink-subtle hover:text-ink"
          aria-label={t('projects:comments_ai_modal.copy_suggested')}
          title={t('projects:comments_ai_modal.copy_suggested')}
        >
          <Copy className="w-3 h-3" />
          {t('projects:comments_ai_modal.copy_suggested')}
        </button>
      </footer>
    </div>
  )
}

function DiffView({
  diff,
  t,
}: {
  diff: ReturnType<typeof diffLines>
  t: ReturnType<typeof useTranslation>['t']
}) {
  if (diff.length === 0) {
    return (
      <div className="rounded border border-line bg-surface-muted px-3 py-4 text-center text-[11px] text-ink-subtle">
        {t('projects:comments_ai_modal.diff_empty')}
      </div>
    )
  }
  return (
    <div className="max-h-72 overflow-y-auto rounded border border-line bg-canvas font-mono text-[11px] leading-snug">
      {diff.map((line, idx) => {
        const base = 'px-2 py-0.5 whitespace-pre-wrap break-words flex gap-2'
        if (line.type === 'add') {
          return (
            <div
              key={`add-${idx}`}
              className={`${base} bg-official-soft/60 text-official`}
            >
              <span className="select-none text-official/70 shrink-0">+</span>
              <span className="break-all">{line.text || ' '}</span>
            </div>
          )
        }
        if (line.type === 'del') {
          return (
            <div
              key={`del-${idx}`}
              className={`${base} bg-critical-soft/60 text-critical`}
            >
              <span className="select-none text-critical/70 shrink-0">−</span>
              <span className="break-all line-through opacity-80">
                {line.text || ' '}
              </span>
            </div>
          )
        }
        return (
          <div key={`eq-${idx}`} className={`${base} text-ink-muted`}>
            <span className="select-none text-ink-subtle/40 shrink-0">
              {' '}
            </span>
            <span className="break-all">{line.text || ' '}</span>
          </div>
        )
      })}
    </div>
  )
}

function ResultFooter({
  canRefine,
  isWorking,
  onBack,
  onApply,
  onStartFresh,
  onCancel,
  applyPending,
  discardPending,
  t,
}: {
  canRefine: boolean
  isWorking: boolean
  onBack: () => void
  onApply: () => void
  onStartFresh: () => void
  onCancel: () => void
  applyPending: boolean
  discardPending: boolean
  t: ReturnType<typeof useTranslation>['t']
}) {
  return (
    <>
      {/* "Cancelar" — descarta la cadena y cierra (Fase 7.g Cancelar UX).
          Posición leftmost = acción destructiva con visual de baja
          jerarquía (solo texto, hover destaca peligro). */}
      <button
        type="button"
        onClick={onCancel}
        disabled={isWorking}
        title={t('projects:comments_ai_modal.actions.cancel_hint')}
        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-ink-muted hover:text-critical rounded disabled:opacity-50"
      >
        {discardPending ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : null}
        {t('projects:comments_ai_modal.actions.cancel')}
      </button>
      {/* "Empezar de cero" — siempre disponible. Cuando el user llegó
          al cap de refinements este es el único camino para volver a
          generar; cuando aún puede refinar, es un escape hatch para
          descartar la cadena y arrancar de nuevo con instrucciones
          frescas. NO cierra el modal — el user sigue iterando. */}
      <button
        type="button"
        onClick={onStartFresh}
        disabled={isWorking}
        title={t('projects:comments_ai_modal.actions.start_fresh_hint')}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-ink-muted hover:text-ink rounded disabled:opacity-50"
      >
        <RefreshCw className="w-3.5 h-3.5" />
        {t('projects:comments_ai_modal.actions.start_fresh')}
      </button>
      <button
        type="button"
        onClick={onBack}
        disabled={isWorking || !canRefine}
        title={
          canRefine ? undefined : t('projects:comments_ai_modal.cap_reached')
        }
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-line rounded text-ink hover:bg-surface-muted disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {t('projects:comments_ai_modal.actions.refine')}
      </button>
      <button
        type="button"
        onClick={onApply}
        disabled={isWorking}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-official hover:bg-official-deep text-canvas text-xs rounded disabled:opacity-50"
      >
        {applyPending ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Check className="w-3.5 h-3.5" />
        )}
        {t('projects:comments_ai_modal.actions.apply')}
      </button>
    </>
  )
}

/**
 * Doc 25 §F2.6 — los tokens reales se leen de `llm_usage_records`
 * via `useUsageRecordByRequest`. Los campos `tokens_input/output` que
 * vienen en `suggestion` son deprecated (default 0 desde F2.5). Si
 * `request_id` está presente, consultamos el endpoint nuevo. Si no
 * (row legacy pre-Migración D), caemos al fallback que llega 0.
 */
function ResultFooterTokens({
  requestId,
  fallbackInput,
  fallbackOutput,
  t,
}: {
  requestId: string | null
  fallbackInput: number
  fallbackOutput: number
  t: ReturnType<typeof useTranslation>['t']
}) {
  const usage = useUsageRecordByRequest({ requestId })
  const input = usage.data?.tokens_input ?? fallbackInput
  const output = usage.data?.tokens_output ?? fallbackOutput
  const total = input + output
  return (
    <span>
      {t('projects:comments_ai_modal.tokens_label', {
        input,
        output,
        total,
      })}
    </span>
  )
}


function RefinementBadge({
  depth,
  cap,
  t,
}: {
  depth: number
  cap: number
  t: ReturnType<typeof useTranslation>['t']
}) {
  if (depth === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-ink-subtle">
        {t('projects:comments_ai_modal.iteration_initial')}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-brand">
      {t('projects:comments_ai_modal.iteration_n', { n: depth, max: cap })}
    </span>
  )
}

// ─── ToolTraceList (Doc 21 Fase 7.g Paso 1) ──────────────────────────────
/**
 * Lista de tools que invocó el agente antes de proponer su rewrite.
 * Transparencia para el usuario: ve qué consultó la IA y por qué los
 * tokens son "X" en lugar de "Y" (cada call cuenta context window).
 *
 * Cases:
 *   - trace=null    → row pre-Fase 7.g (sin tracking). No render.
 *   - trace=[]      → el agente respondió directo sin tools. Mensaje
 *                     informativo: "La IA resolvió con el contexto local".
 *   - trace=[...]   → lista con icon + label legible por cada call.
 */
function ToolTraceList({
  trace,
  t,
}: {
  trace: CommentAiToolTraceEntry[] | null
  t: ReturnType<typeof useTranslation>['t']
}) {
  if (trace === null) return null

  if (trace.length === 0) {
    return (
      <section className="rounded-md border border-line bg-surface-muted px-3 py-2">
        <p className="text-[10px] uppercase tracking-wider font-medium text-ink-subtle mb-1">
          {t('projects:comments_ai_modal.tools_label')}
        </p>
        <p className="text-[11px] text-ink-muted italic">
          {t('projects:comments_ai_modal.tools_none')}
        </p>
      </section>
    )
  }

  return (
    <section className="rounded-md border border-line bg-surface-muted px-3 py-2">
      <p className="text-[10px] uppercase tracking-wider font-medium text-ink-subtle mb-1.5">
        {t('projects:comments_ai_modal.tools_label')}
      </p>
      <ul className="space-y-1">
        {trace.map((entry, idx) => (
          <li
            key={`${entry.iteration}-${entry.tool}-${idx}`}
            className="flex items-start gap-1.5 text-[11px] text-ink"
          >
            <ToolIcon tool={entry.tool} />
            <span className="break-words">
              {formatToolEntry(entry, t)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

function ToolIcon({ tool }: { tool: string }) {
  if (tool === 'auto_retrieve') {
    // Pre-LLM RAG forzado por el service (Fase 7.h). Icon = Sparkles
    // (representa "automatización"), color brand.
    return (
      <Sparkles
        className="w-3 h-3 mt-0.5 shrink-0 text-brand"
        aria-hidden="true"
      />
    )
  }
  if (tool === 'search_knowledge') {
    return (
      <Search
        className="w-3 h-3 mt-0.5 shrink-0 text-brand"
        aria-hidden="true"
      />
    )
  }
  if (tool === 'read_section') {
    return (
      <BookOpen
        className="w-3 h-3 mt-0.5 shrink-0 text-brand"
        aria-hidden="true"
      />
    )
  }
  // Fallback para tools futuros (Paso 2 puede agregar list_artifacts, etc.)
  return (
    <FileText
      className="w-3 h-3 mt-0.5 shrink-0 text-ink-subtle"
      aria-hidden="true"
    />
  )
}

function formatToolEntry(
  entry: CommentAiToolTraceEntry,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  if (entry.tool === 'auto_retrieve') {
    // Fase 7.h — el service hace RAG forzado pre-LLM. Hay 3 sub-casos
    // según `skipped_reason` y los hits encontrados.
    const skipped = entry.args['skipped_reason'] as string | null
    const query = (entry.args['synthesized_query'] as string | null) ?? ''
    const canonHits = Number(entry.args['canon_hits'] ?? 0)
    const sourceHits = Number(entry.args['source_hits'] ?? 0)

    if (skipped === 'no_search_needed') {
      return t('projects:comments_ai_modal.tools_auto_retrieve_skipped')
    }
    if (skipped === 'no_results' || skipped === 'empty_query') {
      return t('projects:comments_ai_modal.tools_auto_retrieve_no_results', {
        query,
      })
    }
    if (skipped === 'synthesis_failed' || skipped === 'retrieval_failed') {
      return t('projects:comments_ai_modal.tools_auto_retrieve_failed')
    }
    // Caso happy: trae hits.
    return t('projects:comments_ai_modal.tools_auto_retrieve_ok', {
      query,
      total: canonHits + sourceHits,
      canon: canonHits,
      sources: sourceHits,
    })
  }
  if (entry.tool === 'search_knowledge') {
    const query = String(entry.args['query'] ?? '')
    const scope = String(entry.args['scope'] ?? 'both')
    return t('projects:comments_ai_modal.tools_search_knowledge', {
      query,
      scope: t(
        `projects:comments_ai_modal.tools_scope.${scope}` as 'projects:comments_ai_modal.tools_scope.both',
        { defaultValue: scope },
      ),
    })
  }
  if (entry.tool === 'read_section') {
    const sectionId = String(entry.args['section_id'] ?? '')
    return t('projects:comments_ai_modal.tools_read_section', {
      section_id: sectionId,
    })
  }
  // Tool desconocida — render genérico para no romper la UI.
  return `${entry.tool}(${JSON.stringify(entry.args)})`
}


// ─── Clipboard helper ─────────────────────────────────────────────────────
async function copyToClipboard(text: string): Promise<void> {
  // navigator.clipboard requiere HTTPS o localhost; en dev funciona. En
  // entornos restringidos cae al fallback de execCommand.
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text)
      return
    }
  } catch {
    // fall through
  }
  const ta = document.createElement('textarea')
  ta.value = text
  ta.setAttribute('readonly', '')
  ta.style.position = 'absolute'
  ta.style.left = '-9999px'
  document.body.appendChild(ta)
  ta.select()
  try {
    document.execCommand('copy')
  } finally {
    document.body.removeChild(ta)
  }
}
